/**
 * One other human, rendered.
 *
 * A remote player is an `ai` Agent spawned in puppet mode (`{ remote: true }`):
 * the same skinned soldier, the same animator, the same per-bone hit capsules —
 * so a remote player is shot with exactly the code that shoots an AI, and
 * headshots are headshots for the same reason. What `remote: true` switches off
 * is the brain: perception, navigation and firing never run, because this
 * body's pose comes off the wire.
 *
 * INTERPOLATION. Snapshots land at 20 Hz with jitter, and rendering the newest
 * one immediately means a body that stutters and teleports. So we play back
 * `INTERP_MS` in the past (the server tells us how far), always between two
 * samples we already hold. The cost is that everyone else is shown ~100 ms
 * behind where the server thinks they are, which is why the server does its hit
 * validation against a *history* of positions rather than the current one.
 *
 * Allocation: the sample ring and every vector are built in the constructor.
 * Nothing here allocates per frame (ARCHITECTURE.md rule 5).
 */

import * as THREE from 'three';

/** ~1.2 s of history at 20 Hz. */
const RING = 24;
/** How far past the newest sample we will guess before freezing the body. */
const MAX_EXTRAPOLATE = 0.2;
/** Corpses linger this long after death, then the agent is disposed. */
const CORPSE_LIFE = 9.0;

const TAU = Math.PI * 2;

/** Shortest-way-round angle lerp. */
function lerpAngle(a, b, t) {
  let d = (b - a) % TAU;
  if (d > Math.PI) d -= TAU;
  else if (d < -Math.PI) d += TAU;
  return a + d * t;
}

export class RemotePlayer {
  constructor(net, ctx, info, pos, yaw) {
    this.net = net;
    this.ctx = ctx;
    this.id = info.id;
    this.name = info.name;
    this.team = info.team ?? 0;
    this.info = info;
    this.alive = info.alive !== false;
    this.health = 1000;

    // ---- sample ring ---------------------------------------------------
    this.ring = new Array(RING);
    for (let i = 0; i < RING; i++) {
      this.ring[i] = { t: 0, x: 0, y: 0, z: 0, yaw: 0, pitch: 0, st: 0, fl: 0, hp: 100, al: true };
    }
    this.head = 0; // next write slot
    this.count = 0;

    // ---- live pose -------------------------------------------------------
    this.position = new THREE.Vector3(pos?.[0] ?? 0, pos?.[1] ?? 0, pos?.[2] ?? 0);
    this.prev = this.position.clone();
    this.yaw = yaw ?? 0;
    this.pitch = 0;
    this.crouch = false;
    this.speed = 0;
    this.flags = 0;
    this._aim = new THREE.Vector3();
    this._v = new THREE.Vector3();
    this._dir = new THREE.Vector3();
    /** Set while the body is being driven from an extrapolated guess. */
    this.stale = false;

    this.agent = null;
    this.corpse = null;
    this.corpseTime = 0;
    this._spawnAgent(this.position, this.yaw);
  }

  /* ==================================================================== */
  /* body                                                                 */
  /* ==================================================================== */

  _spawnAgent(pos, yaw) {
    const ai = this.ctx.peek('ai');
    if (!ai || typeof ai.spawn !== 'function') return;
    // Two silhouettes so friend and enemy read apart at range, which is the
    // only visual team identity the game has.
    const variant = this.team === 0 ? 'vanguard' : 'irregular';
    let agent;
    try {
      agent = ai.spawn(variant, pos, yaw, {
        remote: true,
        team: this.team,
        name: this.name,
      });
    } catch (err) {
      console.error('[net] could not spawn a body for', this.name, err);
      return;
    }
    agent.netId = this.id;
    agent.isRemotePlayer = true;
    agent.name = this.name;
    agent.aimWeight = 1;
    agent.hasTarget = true;
    agent.targetVisible = true;
    this.agent = agent;
  }

  /** Server says this player died. Hand the live pose to the ragdoll solver. */
  kill(point, dir) {
    this.alive = false;
    this.health = 0;
    const a = this.agent;
    if (!a || !a.alive) return;
    this._v.copy(point ?? this.position);
    if (!point) this._v.y += 1.2;
    this._dir.copy(dir ?? this._dir.set(0, 0, 1));
    if (this._dir.lengthSq() < 1e-6) this._dir.set(0, 0, 1);
    try {
      a.die(this._v, this._dir, 45);
    } catch (err) {
      console.error('[net] death handoff failed', err);
    }
    this.corpse = a;
    this.corpseTime = 0;
    this.agent = null;
  }

  respawn(pos, yaw) {
    this.alive = true;
    this.health = 1000;
    this.position.set(pos?.[0] ?? 0, pos?.[1] ?? 0, pos?.[2] ?? 0);
    this.prev.copy(this.position);
    this.yaw = yaw ?? 0;
    this.count = 0;
    this.head = 0;
    this.speed = 0;
    if (!this.agent) this._spawnAgent(this.position, this.yaw);
  }

  /* ==================================================================== */
  /* wire                                                                 */
  /* ==================================================================== */

  /** Store one snapshot sample. `t` is server time in seconds. */
  push(t, s) {
    // Out-of-order or duplicate: Redis relays and reconnects can both produce
    // one. Keeping it would make the interpolator walk backwards.
    if (this.count > 0 && t <= this._at(this.count - 1).t) return;
    const e = this.ring[this.head];
    e.t = t;
    e.x = s.p[0];
    e.y = s.p[1];
    e.z = s.p[2];
    e.yaw = s.y;
    e.pitch = s.pt;
    e.st = s.st;
    e.fl = s.fl;
    e.hp = s.hp;
    e.al = s.al;
    this.head = (this.head + 1) % RING;
    if (this.count < RING) this.count++;
    this.health = s.hp;
  }

  /** Logical index 0 = oldest held sample. */
  _at(i) {
    return this.ring[(this.head - this.count + i + RING * 2) % RING];
  }

  /* ==================================================================== */
  /* frame                                                                */
  /* ==================================================================== */

  /**
   * @param {number} dt
   * @param {number} renderTime  server-clock time to display, already delayed
   */
  update(dt, renderTime) {
    if (this.corpse) {
      this.corpseTime += dt;
      if (this.corpseTime > CORPSE_LIFE) this._disposeCorpse();
    }
    const a = this.agent;
    if (!a || !a.alive || this.count === 0) return;

    this.prev.copy(this.position);
    this._sample(renderTime);

    // Ground speed drives the walk/run blend. Taken from the interpolated
    // positions rather than the reported velocity so the legs always match the
    // distance the body actually covers on screen.
    const moved = Math.hypot(this.position.x - this.prev.x, this.position.z - this.prev.z);
    const inst = dt > 1e-4 ? moved / dt : 0;
    this.speed += (Math.min(inst, 9) - this.speed) * Math.min(1, dt * 12);
    if (this.speed < 0.05) this.speed = 0;

    // Where they are looking, 25 m out. The animator aims the spine and head at
    // this, so a remote player visibly tracks their own crosshair.
    const cp = Math.cos(this.pitch);
    this._aim.set(
      this.position.x - Math.sin(this.yaw) * cp * 25,
      this.position.y + 1.55 + Math.sin(this.pitch) * 25,
      this.position.z - Math.cos(this.yaw) * cp * 25
    );

    a.position.copy(this.position);
    a.yaw = this.yaw;
    a.speed = this.speed;
    a.crouch = this.crouch;
    a.aimTarget.copy(this._aim);
    a.lastKnown.copy(this._aim);
    a.lastKnownAge = 0;
    a.aimWeight = 1;
    a.hasTarget = true;
    a.health = Math.max(1, this.health);
    // Keep the physics capsule with the body: `ai` pushes the per-bone hit
    // capsules from the animated skeleton, but the controller is what stops a
    // corpse spawning inside the floor on the death hand-off.
    a.controller?.teleport(this.position.x, this.position.y, this.position.z);
    a._drive(dt);
  }

  /** Interpolate the ring onto `rt`, writing position/yaw/pitch/crouch. */
  _sample(rt) {
    const n = this.count;
    const newest = this._at(n - 1);
    const oldest = this._at(0);

    if (n === 1 || rt <= oldest.t) {
      const e = n === 1 ? newest : oldest;
      this.position.set(e.x, e.y, e.z);
      this.yaw = e.yaw;
      this.pitch = e.pitch;
      this.crouch = e.st > 0;
      this.flags = e.fl;
      this.stale = false;
      return;
    }

    if (rt >= newest.t) {
      // Past the newest sample: their packets are late. Carry on along the last
      // known heading for a fraction of a second — a body that keeps walking
      // and then stops reads far better than one that stutters — then freeze.
      const ahead = Math.min(rt - newest.t, MAX_EXTRAPOLATE);
      const prev = this._at(n - 2);
      const span = newest.t - prev.t;
      this.stale = rt - newest.t > MAX_EXTRAPOLATE;
      if (span > 1e-4 && ahead > 0) {
        const k = ahead / span;
        this.position.set(
          newest.x + (newest.x - prev.x) * k,
          newest.y + (newest.y - prev.y) * k,
          newest.z + (newest.z - prev.z) * k
        );
      } else {
        this.position.set(newest.x, newest.y, newest.z);
      }
      this.yaw = newest.yaw;
      this.pitch = newest.pitch;
      this.crouch = newest.st > 0;
      this.flags = newest.fl;
      return;
    }

    this.stale = false;
    // Walk back from the newest: the sample we want is nearly always the last
    // pair, so this loop runs once or twice.
    for (let i = n - 1; i > 0; i--) {
      const b = this._at(i);
      if (b.t < rt) continue;
      const a = this._at(i - 1);
      const span = b.t - a.t;
      const k = span > 1e-5 ? (rt - a.t) / span : 0;
      this.position.set(a.x + (b.x - a.x) * k, a.y + (b.y - a.y) * k, a.z + (b.z - a.z) * k);
      this.yaw = lerpAngle(a.yaw, b.yaw, k);
      this.pitch = a.pitch + (b.pitch - a.pitch) * k;
      this.crouch = (k < 0.5 ? a.st : b.st) > 0;
      this.flags = k < 0.5 ? a.fl : b.fl;
      return;
    }
  }

  /* ==================================================================== */

  _disposeCorpse() {
    const ai = this.ctx.peek('ai');
    const c = this.corpse;
    this.corpse = null;
    if (!c) return;
    const i = ai?.agents?.indexOf(c) ?? -1;
    if (i >= 0) ai.agents.splice(i, 1);
    try {
      c.dispose();
    } catch (err) {
      console.warn('[net] corpse dispose failed', err);
    }
  }

  dispose() {
    const ai = this.ctx.peek('ai');
    for (const a of [this.agent, this.corpse]) {
      if (!a) continue;
      const i = ai?.agents?.indexOf(a) ?? -1;
      if (i >= 0) ai.agents.splice(i, 1);
      try {
        a.dispose();
      } catch (err) {
        console.warn('[net] dispose failed', err);
      }
    }
    this.agent = null;
    this.corpse = null;
  }
}
